export type AssistantModuleBundle = typeof import('./assistant.module');

export const ASSISTANT_MODULE_LOAD_TIMEOUT_MS = 12000;

export class AssistantModuleLoadTimeoutError extends Error {
  constructor(public readonly timeoutMs: number) {
    super(`Hope assistant module did not settle within ${timeoutMs}ms`);
    this.name = 'AssistantModuleLoadTimeoutError';
  }
}

export type AssistantImportRecoveryAction = 'auto_reload' | 'fallback_form' | 'stay_on_form';

export interface AssistantImportFailureDetail {
  action: AssistantImportRecoveryAction;
  source: string;
  appBooted: boolean;
  retryPresent: boolean;
}

export interface AssistantImportRecoveryEnvironment {
  initialSearch: string;
  initialHref: string;
  appBooted: () => boolean;
  notify: (detail: AssistantImportFailureDetail) => void;
  schedule: (callback: () => void, delayMs: number) => void;
  replace: (url: string) => void;
}

export interface AssistantModuleLoaderOptions {
  timeoutMs?: number;
  schedule?: (callback: () => void, delayMs: number) => unknown;
  cancel?: (handle: unknown) => void;
}

export interface ActivePageTimeoutEnvironment {
  now: () => number;
  hidden: () => boolean;
  schedule: (callback: () => void, delayMs: number) => unknown;
  cancel: (handle: unknown) => void;
  listen: (callback: () => void) => () => void;
  deferVisibleStart?: (callback: () => void) => void;
}

export interface CancellableAssistantTimeout {
  cancel: () => void;
}

// iOS suspends JavaScript while the Google app or Safari is backgrounded. A wall-clock timer that
// expires during that suspension can win the resume race against an already-downloaded module and
// falsely reload a healthy visit. Count only time while the document is visible.
export function createActivePageTimeout(
  callback: () => void,
  delayMs: number,
  environment: ActivePageTimeoutEnvironment,
): CancellableAssistantTimeout {
  let remainingMs = Math.max(0, delayMs);
  let startedAt: number | null = null;
  let timerHandle: unknown;
  let finished = false;
  let stopListening = () => undefined;

  const subtractElapsed = (): void => {
    if (startedAt === null) { return; }
    remainingMs = Math.max(0, remainingMs - Math.max(0, environment.now() - startedAt));
    startedAt = null;
  };

  const stopTimer = (): void => {
    if (timerHandle !== undefined) {
      try { environment.cancel(timerHandle); } catch (error) { /* timer cleanup is non-fatal */ }
      timerHandle = undefined;
    }
    subtractElapsed();
  };

  const finish = (): void => {
    if (finished) { return; }
    finished = true;
    stopListening();
    callback();
  };

  const startTimer = (): void => {
    if (finished || environment.hidden() || timerHandle !== undefined) { return; }
    startedAt = environment.now();
    timerHandle = environment.schedule(() => {
      timerHandle = undefined;
      subtractElapsed();
      if (environment.hidden()) { return; }
      finish();
    }, remainingMs);
  };

  const onVisibilityChange = (): void => {
    if (environment.hidden()) { stopTimer(); }
    else if (environment.deferVisibleStart) { environment.deferVisibleStart(startTimer); }
    else { startTimer(); }
  };

  stopListening = environment.listen(onVisibilityChange);
  startTimer();

  return {
    cancel: () => {
      if (finished) { return; }
      stopTimer();
      finished = true;
      stopListening();
    },
  };
}

export function createAssistantModuleLoader<T>(
  importer: () => Promise<T>,
  options: AssistantModuleLoaderOptions = {},
): () => Promise<T> {
  let request: Promise<T> | null = null;

  return () => {
    if (!request) {
      try {
        const imported = Promise.resolve(importer());
        const timeoutMs = Number(options.timeoutMs || 0);
        if (timeoutMs <= 0) {
          request = imported;
        } else {
          const schedule = options.schedule || ((callback, delayMs) => setTimeout(callback, delayMs));
          const cancel = options.cancel || ((handle) => clearTimeout(handle as number));
          request = new Promise<T>((resolve, reject) => {
            let settled = false;
            let timerHandle: unknown;

            const settle = (callback: () => void): void => {
              if (settled) { return; }
              settled = true;
              if (timerHandle !== undefined) {
                try { cancel(timerHandle); } catch (error) { /* timer cleanup is non-fatal */ }
              }
              callback();
            };

            imported.then(
              value => settle(() => resolve(value)),
              error => settle(() => reject(error)),
            );

            try {
              timerHandle = schedule(
                () => settle(() => reject(new AssistantModuleLoadTimeoutError(timeoutMs))),
                timeoutMs,
              );
            } catch (error) {
              settle(() => reject(error));
            }
          });
        }
      } catch (error) {
        request = Promise.reject(error);
      }
    }
    return request;
  };
}

export function createAssistantFailureReportClaim(): () => boolean {
  let claimed = false;
  return () => {
    if (claimed) { return false; }
    claimed = true;
    return true;
  };
}

function isDocumentInitialAssistant(search: string): boolean {
  return /[?&]startAssistant=1(?:&|$)/i.test(search || '');
}

function hasAssistantRetry(search: string): boolean {
  return /[?&]hbretry=1(?:&|$)/i.test(search || '');
}

function retryUrl(href: string): string {
  const url = new URL(href);
  url.searchParams.set('hbretry', '1');
  return url.pathname + url.search + url.hash;
}

export function createAssistantImportRecovery(environment: AssistantImportRecoveryEnvironment):
  (source: string) => AssistantImportRecoveryAction {
  const documentInitialAssistant = isDocumentInitialAssistant(environment.initialSearch);
  const retryPresent = hasAssistantRetry(environment.initialSearch);
  let settledAction: AssistantImportRecoveryAction | null = null;

  return (source: string): AssistantImportRecoveryAction => {
    if (settledAction) {
      return settledAction;
    }

    settledAction = documentInitialAssistant
      ? (retryPresent ? 'fallback_form' : 'auto_reload')
      : 'stay_on_form';

    const detail: AssistantImportFailureDetail = {
      action: settledAction,
      source,
      appBooted: environment.appBooted(),
      retryPresent,
    };
    environment.notify(detail);

    if (settledAction === 'auto_reload') {
      // Use the document-initial URL captured before Angular can normalize the current URL.
      // This preserves every ad/product parameter and the hash exactly.
      const destination = retryUrl(environment.initialHref);
      environment.schedule(() => environment.replace(destination), 250);
    }

    return settledAction;
  };
}

export const loadAssistantModuleOnce = createAssistantModuleLoader<AssistantModuleBundle>(
  () => import('./assistant.module'),
  {
    timeoutMs: ASSISTANT_MODULE_LOAD_TIMEOUT_MS,
    schedule: (callback, delayMs) => createActivePageTimeout(callback, delayMs, {
      now: () => Date.now(),
      hidden: () => document.visibilityState === 'hidden',
      schedule: (timerCallback, timerDelayMs) => window.setTimeout(timerCallback, timerDelayMs),
      cancel: handle => window.clearTimeout(handle as number),
      listen: visibilityCallback => {
        document.addEventListener('visibilitychange', visibilityCallback);
        return () => document.removeEventListener('visibilitychange', visibilityCallback);
      },
      // Let queued module evaluation/promise work settle before rearming after an iOS app switch.
      // A still-pending import retains its remaining foreground budget on the next paint.
      deferVisibleStart: callback => {
        Promise.resolve().then(() => {
          if (typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(() => callback());
          } else {
            window.setTimeout(callback, 0);
          }
        });
      },
    }),
    cancel: handle => (handle as CancellableAssistantTimeout).cancel(),
  },
);
export const claimAssistantFailureReport = createAssistantFailureReportClaim();

const initialSearch = typeof window !== 'undefined' ? window.location.search : '';
const initialHref = typeof window !== 'undefined' ? window.location.href : '';
const recoverAssistantImport = createAssistantImportRecovery({
  initialSearch,
  initialHref,
  appBooted: () => !!(window as any).__fruAppBooted,
  notify: (detail) => {
    try {
      (window as any).__hopeAssistantImportFailed = detail;
      window.dispatchEvent(new CustomEvent('hopeAssistantImportFailed', { detail }));
    } catch (error) { /* telemetry and the boot overlay are non-fatal */ }
  },
  schedule: (callback, delayMs) => { window.setTimeout(callback, delayMs); },
  replace: (url) => { window.location.replace(url); },
});

export function handleAssistantImportFailure(source: string): AssistantImportRecoveryAction {
  return recoverAssistantImport(source);
}
